import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  FileText,
  Download,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Eye,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { certificatesService } from '../../services/certificates';
import CustomButton from '../ui/CustomButton';

const PDFViewer = ({
  certificateId,
  certificate,
  isDarkMode,
  height = '600px',
  showControls = true,
  autoLoad = true,
}) => {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const maxRetries = 3;
  const isDraft = certificate?.IsDraft;

  // Load PDF when component mounts or certificate changes
  useEffect(() => {
    if (autoLoad && certificateId) {
      loadPDF();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateId, isDraft, autoLoad]);

  // Clean up blob URL when component unmounts or PDF changes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const loadPDF = async () => {
    if (!certificateId) {
      setError('Certificate ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Clean up previous blob URL
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }

    try {
      // Use the getCertificatePDF method that returns a blob
      const blob = await certificatesService.getCertificatePDF(
        certificateId,
        isDraft // isDemo parameter
      );

      // Create URL from blob for viewing
      const url = window.URL.createObjectURL(blob);

      setPdfBlob(blob);
      setPdfUrl(url);
      setRetryCount(0);
    } catch (err) {
      console.error('Error loading PDF:', err);

      // Provide more specific error messages
      let errorMessage = 'Failed to load PDF';
      if (err.message?.includes('Demo PDF not available')) {
        errorMessage = 'Draft PDF is being generated...';
      } else if (err.message?.includes('No PDF available')) {
        errorMessage = 'PDF is not ready yet';
      } else if (err.message?.includes('network')) {
        errorMessage = 'Network error - check your connection';
      } else if (err.response?.status === 404) {
        errorMessage = 'PDF not found - it may still be generating';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error - please try again later';
      } else {
        errorMessage = err.message || 'Failed to load PDF';
      }

      setError(errorMessage);

      // Auto-retry logic for network errors
      if (
        retryCount < maxRetries &&
        (err.message?.includes('network') ||
          err.message?.includes('Failed to fetch') ||
          err.response?.status >= 500)
      ) {
        setTimeout(
          () => {
            setRetryCount(prev => prev + 1);
            loadPDF();
          },
          2000 * (retryCount + 1)
        ); // Exponential backoff
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      let blob = pdfBlob;

      // If we don't have the blob cached, fetch it again
      if (!blob) {
        blob = await certificatesService.getCertificatePDF(
          certificateId,
          isDraft
        );
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Create appropriate filename
      const baseFileName =
        certificate?.CocNumber || `certificate-${certificateId}`;
      const suffix = isDraft ? '_draft' : '';
      link.download = `${baseFileName}${suffix}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download PDF');
    }
  };

  const openInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  if (error && retryCount >= maxRetries) {
    return (
      <Card
        sx={{
          height,
          background: isDarkMode
            ? 'rgba(30, 41, 59, 0.6)'
            : 'rgba(255, 255, 255, 0.8)',
          border: '2px dashed #ef4444',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <AlertCircle size={48} color="#ef4444" style={{ marginBottom: 16 }} />
          <Typography
            variant="h6"
            sx={{ color: '#ef4444', mb: 2, fontWeight: 600 }}
          >
            PDF Load Error
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 3 }}
          >
            {error}
          </Typography>
          <CustomButton
            variant="outlined"
            onClick={loadPDF}
            startIcon={<RefreshCw size={16} />}
            sx={{
              borderColor: '#ef4444',
              color: '#ef4444',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            Retry Loading
          </CustomButton>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card
        sx={{
          height,
          background: isDarkMode
            ? 'rgba(30, 41, 59, 0.6)'
            : 'rgba(255, 255, 255, 0.8)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <CircularProgress
            size={48}
            sx={{
              color: '#3b82f6',
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{ color: isDarkMode ? '#f1f5f9' : '#1e293b', mb: 1 }}
          >
            Loading PDF...
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
          >
            {retryCount > 0 && `Retry attempt ${retryCount}/${maxRetries}`}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? '#64748b' : '#94a3b8',
              display: 'block',
              mt: 1,
            }}
          >
            {isDraft
              ? 'Loading draft version...'
              : 'Loading production version...'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          height,
          background: isDarkMode
            ? 'rgba(30, 41, 59, 0.6)'
            : 'rgba(255, 255, 255, 0.8)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* PDF Viewer Header */}
        {showControls && (
          <Box
            sx={{
              p: 2,
              borderBottom: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: isDarkMode
                ? 'rgba(15, 23, 42, 0.8)'
                : 'rgba(248, 250, 252, 0.8)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileText size={20} color="#3b82f6" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                }}
              >
                Certificate PDF
              </Typography>
              {isDraft && (
                <Chip
                  label="DRAFT VERSION"
                  size="small"
                  sx={{
                    background:
                      'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              )}
              {!isDraft && (
                <Chip
                  label="PRODUCTION"
                  size="small"
                  sx={{
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Refresh PDF">
                <IconButton
                  size="small"
                  onClick={loadPDF}
                  sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  <RefreshCw size={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  onClick={openInNewTab}
                  disabled={!pdfUrl}
                  sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  <ExternalLink size={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Download PDF">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={!pdfBlob && !certificateId}
                  sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  <Download size={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fullscreen">
                <IconButton
                  size="small"
                  onClick={toggleFullscreen}
                  disabled={!pdfUrl}
                  sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                  <Maximize2 size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* PDF Content */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {pdfUrl ? (
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
              width="100%"
              height="100%"
              style={{
                border: 'none',
                background: isDarkMode ? '#1e293b' : '#ffffff',
              }}
              title="Certificate PDF"
              onError={e => {
                setError('Failed to load PDF in viewer');
                console.error('PDF iframe error:', e);
              }}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Eye size={48} color={isDarkMode ? '#64748b' : '#94a3b8'} />
                <Typography
                  variant="h6"
                  sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mt: 2 }}
                >
                  No PDF Available
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', mt: 1 }}
                >
                  {isDraft
                    ? 'Draft PDF has not been generated yet'
                    : 'Production PDF has not been generated yet'}
                </Typography>
                <CustomButton
                  variant="outlined"
                  onClick={loadPDF}
                  startIcon={<RefreshCw size={16} />}
                  sx={{ mt: 2 }}
                >
                  Try Loading
                </CustomButton>
              </Box>
            </Box>
          )}
        </Box>

        {/* Status Bar */}
        {pdfBlob && (
          <Box
            sx={{
              p: 1,
              borderTop: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              background: isDarkMode
                ? 'rgba(15, 23, 42, 0.5)'
                : 'rgba(248, 250, 252, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
            >
              {certificate?.CocNumber || `Draft-${certificateId}`}.pdf (
              {Math.round(pdfBlob.size / 1024)} KB)
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDraft ? '#f59e0b' : '#10b981',
                  fontWeight: 500,
                }}
              >
                {isDraft ? '📝 Draft Version' : '✅ Production Version'}
              </Typography>
            </Box>
          </Box>
        )}
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={toggleFullscreen}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            background: isDarkMode ? '#0f172a' : '#ffffff',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileText size={24} color="#3b82f6" />
            <Typography variant="h6">
              Certificate PDF - {certificate?.CocNumber || 'Draft'}
            </Typography>
            {isDraft && (
              <Chip
                label="DRAFT VERSION"
                size="small"
                sx={{
                  background:
                    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: 'calc(100vh - 120px)' }}>
          {pdfUrl && (
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Certificate PDF - Fullscreen"
            />
          )}
        </DialogContent>

        <DialogActions>
          <CustomButton
            variant="outlined"
            onClick={handleDownload}
            startIcon={<Download size={16} />}
            disabled={!pdfBlob && !certificateId}
          >
            Download
          </CustomButton>

          <CustomButton
            variant="outlined"
            onClick={toggleFullscreen}
            startIcon={<Minimize2 size={16} />}
          >
            Close Fullscreen
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PDFViewer;
